import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, Minus, Plus, Trash2, ArrowRight, Loader2, ShoppingBag, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => (src && src.startsWith("/") ? `${BACKEND}${src}` : src);
const SIZES = ["7", "8", "9", "10", "11"];

const CartDrawer = () => {
  const { lang } = useLanguage();
  const cart = useCart();
  const [code, setCode] = useState("");
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeStatus, setCodeStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState([]);
  const [pickedSize, setPickedSize] = useState({}); // {slug: size}

  // Pre-fill the code input when a code is already applied (e.g. from URL param FCU15)
  useEffect(() => {
    if (cart.discountCode && !code) {
      setCode(cart.discountCode);
    }
  }, [cart.discountCode, code]);

  // Fetch products once when drawer opens (for inline cross-sell of other models)
  useEffect(() => {
    if (!cart.open || products.length > 0) return;
    axios
      .get(`${API}/products`)
      .then((res) => setProducts(res.data))
      .catch((err) => console.error("products fetch", err));
  }, [cart.open, products.length]);

  const tDe = lang === "de";
  const T = {
    title: tDe ? "Warenkorb" : "Cart",
    empty: tDe ? "Dein Warenkorb ist leer." : "Your cart is empty.",
    empty_cta: tDe ? "Produkte entdecken" : "Browse products",
    size: tDe ? "Größe" : "Size",
    select_size: tDe ? "Bitte Größe wählen" : "Please pick a size",
    subtotal: tDe ? "Zwischensumme" : "Subtotal",
    discount: tDe ? "Rabatt" : "Discount",
    shipping: tDe ? "Versand" : "Shipping",
    free: tDe ? "Gratis" : "Free",
    total: tDe ? "Gesamt" : "Total",
    code_label: tDe ? "Rabattcode" : "Discount code",
    code_apply: tDe ? "Anwenden" : "Apply",
    code_invalid: tDe ? "Code ungültig." : "Invalid code.",
    checkout: tDe ? "Zur Kasse" : "Checkout",
    redirecting: tDe ? "Weiterleitung..." : "Redirecting...",
    secure: tDe ? "Sichere Zahlung via Stripe" : "Secure payment via Stripe",
    free_at: tDe ? `Gratis-Versand ab ${cart.region === "eu" ? "EUR 84" : "CHF 80"}` : `Free shipping over ${cart.region === "eu" ? "EUR 84" : "CHF 80"}`,
  };

  const applyCode = async () => {
    if (!code.trim()) return;
    setCodeChecking(true);
    setCodeStatus(null);
    try {
      const res = await axios.post(`${API}/discount/validate`, { code: code.trim() });
      setCodeStatus(res.data);
      if (res.data.valid) {
        cart.setDiscountCode(code.trim().toUpperCase());
        cart.refreshQuote(code.trim().toUpperCase());
      }
    } catch (err) {
      setCodeStatus({ valid: false });
    } finally {
      setCodeChecking(false);
    }
  };

  const goCheckout = async () => {
    if (cart.items.length === 0) return;
    // require size for all glove items
    for (const it of cart.items) {
      if (!it.size && it.slug !== "glove-bag") {
        toast.error(T.select_size);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        items: cart.items.map((i) => ({ slug: i.slug, size: i.size, quantity: i.quantity })),
        discount_code: cart.discountCode || null,
        origin_url: window.location.origin,
        region: cart.region,
      };
      const res = await axios.post(`${API}/checkout/session`, payload);
      window.location.href = res.data.url;
    } catch (err) {
      console.error(err);
      toast.error(tDe ? "Fehler beim Checkout. Bitte erneut versuchen." : "Checkout error. Try again.");
      setSubmitting(false);
    }
  };

  const currencyCode = (cart.quote?.currency || (cart.region === "eu" ? "eur" : "chf")).toUpperCase();
  const fmt = (n) => `${currencyCode} ${Number(n).toFixed(2)}`;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => cart.setOpen(false)}
        data-testid="cart-backdrop"
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] transition-opacity ${
          cart.open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-[#0A0A0A] border-l border-white/10 z-[70] flex flex-col transition-transform duration-300 ${
          cart.open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <header className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-5 h-5 text-[#00A651]" />
            <h2 className="font-brush text-3xl text-white leading-none">
              {T.title}
              {cart.itemCount > 0 && (
                <span className="ml-2 font-condensed text-sm text-white/50">({cart.itemCount})</span>
              )}
            </h2>
          </div>
          <button
            onClick={() => cart.setOpen(false)}
            data-testid="cart-close"
            className="text-white/60 hover:text-white p-2"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {cart.items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4">
            <ShoppingBag className="w-12 h-12 text-white/20" />
            <p className="text-white/50">{T.empty}</p>
            <button
              onClick={() => cart.setOpen(false)}
              data-testid="cart-empty-cta"
              className="mt-2 inline-flex items-center gap-2 border border-[#00A651] text-[#00A651] hover:bg-[#00A651] hover:text-black font-condensed uppercase tracking-widest text-xs px-4 py-2.5 transition-colors"
            >
              {T.empty_cta} <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {cart.items.map((it) => (
                <div
                  key={`${it.slug}-${it.size}`}
                  data-testid={`cart-item-${it.slug}-${it.size || "nosize"}`}
                  className="flex gap-4 border-b border-white/5 pb-4"
                >
                  {it.image ? (
                    <img
                      src={resolveImg(it.image)}
                      alt={it.name}
                      className="w-20 h-24 object-cover bg-black border border-white/10"
                    />
                  ) : (
                    <div className="w-20 h-24 bg-gradient-to-br from-[#111] to-[#050505] border border-white/10 flex items-center justify-center">
                      <ShoppingBag className="w-7 h-7 text-[#00A651]/60" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-brush text-xl text-white leading-tight">{it.name}</h3>
                      <button
                        onClick={() => cart.removeItem(it.slug, it.size)}
                        data-testid={`cart-remove-${it.slug}`}
                        className="text-white/40 hover:text-red-400 p-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {it.size && (
                      <p className="text-[11px] font-condensed uppercase tracking-widest text-white/50 mt-1">
                        {T.size}: <span className="text-white/80">{it.size}</span>
                      </p>
                    )}
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center border border-white/15">
                        <button
                          onClick={() => cart.setQty(it.slug, it.size, it.quantity - 1)}
                          data-testid={`cart-decr-${it.slug}`}
                          className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-[#00A651]"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm font-display">{it.quantity}</span>
                        <button
                          onClick={() => cart.setQty(it.slug, it.size, it.quantity + 1)}
                          data-testid={`cart-incr-${it.slug}`}
                          className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-[#00A651]"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="text-[#00A651] font-display text-base">
                        {(() => {
                          const qLine = cart.quote?.line_items?.find(
                            (l) => l.slug === it.slug && (l.size || null) === (it.size || null)
                          );
                          const total = qLine?.line_total ?? qLine?.line_total_chf ?? it.price_chf * it.quantity;
                          return fmt(total);
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {/* Cross-sell: add other glove model(s) inline with size picker */}
              {(() => {
                const gloveSlugs = ["mamba-edition", "panther-edition"];
                const missingGloves = products.filter(
                  (p) => gloveSlugs.includes(p.slug) && !cart.items.some((i) => i.slug === p.slug)
                );
                if (missingGloves.length === 0) return null;
                return (
                  <div className="space-y-3 pt-2" data-testid="cart-crosssell-gloves">
                    <div className="font-condensed uppercase tracking-widest text-[10px] text-white/40">
                      {tDe ? "Weiteres Modell hinzufügen" : "Add another model"}
                    </div>
                    {missingGloves.map((p) => {
                      const sz = pickedSize[p.slug];
                      const priceDisplay = cart.region === "eu"
                        ? `EUR ${p.slug === "mamba-edition" ? 104 : 114}`
                        : `CHF ${p.price_chf.toFixed(0)}`;
                      return (
                        <div
                          key={p.slug}
                          data-testid={`cart-add-model-${p.slug}`}
                          className="border border-dashed border-[#00A651]/40 bg-[#00A651]/5 p-3 space-y-2.5"
                        >
                          <div className="flex items-center gap-3">
                            {p.image ? (
                              <img
                                src={resolveImg(p.image)}
                                alt={p.name}
                                className="w-12 h-14 object-cover bg-black border border-white/10 shrink-0"
                              />
                            ) : (
                              <div className="w-12 h-14 bg-black border border-white/10 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="font-brush text-lg text-white leading-tight">
                                + {p.name}
                              </div>
                              <div className="text-[11px] text-white/60 mt-0.5">
                                {priceDisplay} · {tDe ? "Größe wählen" : "Pick size"}
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {SIZES.map((s) => {
                              const stock = p.stock_by_size?.[s];
                              const outOfStock = stock === 0;
                              const picked = sz === s;
                              return (
                                <button
                                  key={s}
                                  onClick={() =>
                                    !outOfStock &&
                                    setPickedSize((prev) => ({ ...prev, [p.slug]: s }))
                                  }
                                  disabled={outOfStock}
                                  data-testid={`cart-crosssell-size-${p.slug}-${s}`}
                                  title={outOfStock ? "Ausverkauft" : `${stock ?? ""} verfügbar`}
                                  className={`w-8 h-8 text-[11px] font-display border transition-colors ${
                                    picked
                                      ? "bg-[#00A651] text-black border-[#00A651]"
                                      : outOfStock
                                      ? "border-white/10 text-white/25 cursor-not-allowed line-through"
                                      : "border-white/15 text-white/70 hover:border-[#00A651] hover:text-white"
                                  }`}
                                >
                                  {s}
                                </button>
                              );
                            })}
                          </div>
                          <button
                            onClick={() => {
                              if (!sz) {
                                toast.error(tDe ? "Bitte Größe wählen." : "Please pick a size.");
                                return;
                              }
                              cart.addItem(p, sz);
                              setPickedSize((prev) => ({ ...prev, [p.slug]: undefined }));
                              toast.success(
                                tDe ? `${p.name} · Gr. ${sz} hinzugefügt` : `${p.name} · size ${sz} added`
                              );
                            }}
                            data-testid={`cart-crosssell-add-${p.slug}`}
                            className="w-full inline-flex items-center justify-center gap-2 border border-[#00A651] text-[#00A651] hover:bg-[#00A651] hover:text-black font-condensed uppercase tracking-widest text-[11px] px-3 py-2 transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                            {tDe ? "In den Warenkorb" : "Add to cart"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Cross-sell: Glove Bag if gloves in cart but not bag */}
              {cart.items.some((i) => i.slug === "mamba-edition" || i.slug === "panther-edition") &&
                !cart.items.some((i) => i.slug === "glove-bag") && (
                  <button
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/products`);
                        const bag = res.data.find((p) => p.slug === "glove-bag");
                        if (bag) {
                          cart.addItem(bag, null);
                          toast.success(
                            tDe ? "Glove Bag hinzugefügt · Bundle-Rabatt aktiv" : "Glove Bag added · bundle discount applied"
                          );
                        }
                      } catch (err) {
                        console.error(err);
                        toast.error(tDe ? "Konnte Tasche nicht hinzufügen." : "Could not add bag.");
                      }
                    }}
                    data-testid="cart-crosssell-bag"
                    className="w-full text-left border border-dashed border-[#00A651]/40 bg-[#00A651]/5 hover:bg-[#00A651]/15 hover:border-[#00A651] transition-colors p-4 flex items-center gap-3 cursor-pointer"
                  >
                    <div className="w-12 h-12 bg-[#00A651]/15 border border-[#00A651]/30 flex items-center justify-center shrink-0">
                      <ShoppingBag className="w-5 h-5 text-[#00A651]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-brush text-lg text-white leading-tight">
                        {tDe ? "+ Glove Bag dazu?" : "+ Add Glove Bag?"}
                      </div>
                      <div className="text-[11px] text-white/60 mt-0.5">
                        {tDe
                          ? `Cordura Bag ${currencyCode} ${cart.region === "eu" ? "30" : "29"} · Bundle spart ${currencyCode} 5`
                          : `Cordura bag ${currencyCode} ${cart.region === "eu" ? "30" : "29"} · save ${currencyCode} 5 in bundle`}
                      </div>
                    </div>
                    <Plus className="w-5 h-5 text-[#00A651] shrink-0" />
                  </button>
              )}
            </div>

            <footer className="border-t border-white/10 p-6 space-y-4 bg-black">
              {/* Ship-to region selector */}
              <div>
                <label className="block font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-2">
                  {tDe ? "Lieferland" : "Ship to"}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => cart.setRegion("ch")}
                    data-testid="cart-region-ch"
                    className={`px-3 py-2 text-xs font-condensed uppercase tracking-widest border transition-colors ${
                      cart.region === "ch"
                        ? "border-[#00A651] bg-[#00A651]/15 text-[#00A651]"
                        : "border-white/15 text-white/70 hover:border-white/40"
                    }`}
                  >
                    🇨🇭 Schweiz / LI · CHF
                  </button>
                  <button
                    type="button"
                    onClick={() => cart.setRegion("eu")}
                    data-testid="cart-region-eu"
                    className={`px-3 py-2 text-xs font-condensed uppercase tracking-widest border transition-colors ${
                      cart.region === "eu"
                        ? "border-[#00A651] bg-[#00A651]/15 text-[#00A651]"
                        : "border-white/15 text-white/70 hover:border-white/40"
                    }`}
                  >
                    🇩🇪 DE · 🇦🇹 AT · EUR
                  </button>
                </div>
                <div className="text-[10px] text-white/40 mt-1.5">
                  {cart.region === "eu"
                    ? (tDe ? "Versand aus Deutschland · DHL · EUR 8.50" : "Ships from Germany · DHL · EUR 8.50")
                    : (tDe ? "Versand mit Schweizerischer Post · CHF 8" : "Ships with Swiss Post · CHF 8")}
                </div>
              </div>

              {/* Discount code */}
              <div>
                <label className="block font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-2">
                  {T.code_label}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.toUpperCase());
                      setCodeStatus(null);
                    }}
                    data-testid="cart-code-input"
                    className="flex-1 bg-[#0A0A0A] border border-white/15 focus:border-[#00A651] outline-none px-3 py-2 text-white text-sm tracking-wider uppercase"
                  />
                  <button
                    onClick={applyCode}
                    disabled={codeChecking || !code.trim()}
                    data-testid="cart-code-apply"
                    className="px-4 font-condensed uppercase tracking-widest text-xs border border-white/20 text-white/80 hover:border-[#00A651] hover:text-[#00A651] disabled:opacity-40"
                  >
                    {codeChecking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : T.code_apply}
                  </button>
                </div>
                {cart.discountCode && cart.quote?.discount_label && (
                  <div data-testid="cart-discount-applied" className="mt-2 inline-flex items-center gap-2 bg-[#00A651]/15 border border-[#00A651]/40 text-[#00A651] px-2.5 py-1 text-[10px] font-condensed uppercase tracking-widest">
                    <CheckCircle2 className="w-3 h-3" />
                    {cart.quote.discount_label} · −{cart.quote.discount_percentage}%
                  </div>
                )}
                {codeStatus && !codeStatus.valid && (
                  <p className="mt-1 text-[10px] text-red-400 font-condensed uppercase tracking-widest">{T.code_invalid}</p>
                )}
              </div>

              {/* Totals */}
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-white/70">
                  <span>{T.subtotal}</span>
                  <span data-testid="cart-subtotal">{fmt(cart.quote?.subtotal ?? 0)}</span>
                </div>
                {cart.quote?.discount_amount > 0 && (
                  <div className="flex justify-between text-[#00A651]">
                    <span>
                      {cart.quote.discount_label || T.discount}
                      <span className="text-white/50 ml-1">({cart.quote.discount_percentage}%)</span>
                    </span>
                    <span data-testid="cart-discount">−{fmt(cart.quote.discount_amount)}</span>
                  </div>
                )}
                {cart.quote?.bundle_discount > 0 && (
                  <div className="flex justify-between text-[#00A651]">
                    <span>{tDe ? "Bundle-Rabatt" : "Bundle discount"}</span>
                    <span data-testid="cart-bundle-discount">−{fmt(cart.quote.bundle_discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-white/70">
                  <span>{T.shipping}</span>
                  <span data-testid="cart-shipping">
                    {cart.quote?.shipping === 0 ? T.free : fmt(cart.quote?.shipping ?? 8)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-white/10">
                  <span className="font-condensed uppercase tracking-widest text-xs text-white/60">{T.total}</span>
                  <span data-testid="cart-total" className="font-display text-2xl text-[#00A651] leading-none">
                    {fmt(cart.quote?.total ?? 0)}
                  </span>
                </div>
                <p className="text-[10px] text-white/40 pt-1">
                  {cart.region === "eu"
                    ? (tDe ? "Alle Preise in EUR" : "All prices in EUR")
                    : (tDe ? "Alle Preise inkl. 8.1% MwSt." : "All prices incl. 8.1% VAT.")} · {T.free_at}
                </p>
              </div>

              <button
                onClick={goCheckout}
                disabled={submitting}
                data-testid="cart-checkout"
                className="w-full inline-flex items-center justify-center gap-3 bg-[#00A651] hover:bg-[#008A43] disabled:opacity-50 text-black font-condensed uppercase tracking-[0.2em] px-6 py-4 transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {T.redirecting}
                  </>
                ) : (
                  <>
                    {T.checkout}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
              <button
                onClick={() => cart.setOpen(false)}
                data-testid="cart-continue-shopping"
                className="w-full text-center text-[11px] font-condensed uppercase tracking-widest text-white/50 hover:text-[#00A651] transition-colors py-1"
              >
                {tDe ? "← Weiter einkaufen" : "← Continue shopping"}
              </button>
              <p className="text-center text-[10px] text-white/30 font-condensed uppercase tracking-widest">
                {T.secure}
              </p>
            </footer>
          </>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
